/* ==========================================================================
   Announcement bar
   Swiper-backed rotation with a progress line, paused on hover, on keyboard
   focus and while the tab is hidden. Reduced-motion visitors get a static bar.
   Degrades safely: if Swiper is missing the first message still renders.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function frameOf(el) {
    return el.closest ? el.closest('[data-woopf-bar]') : null;
  }

  function startProgress(frame) {
    if (!frame || frame.dataset.autoplay !== 'true' || REDUCED.matches) return;
    var line = frame.querySelector('.s-announcement-bar__progress i');
    if (!line) return;
    frame.classList.remove('is-running');
    // Force a reflow so the animation restarts on every slide change.
    void line.offsetWidth;
    frame.classList.add('is-running');
  }

  function setPaused(frame, paused) {
    if (!frame) return;
    frame.classList.toggle('is-paused', !!paused);
    var bar = frame.querySelector('s-announcement-bar');
    var swiper = bar && bar.swiper;
    if (!swiper || !swiper.autoplay) return;
    try {
      if (paused) swiper.autoplay.stop();
      else swiper.autoplay.start();
    } catch (err) {
      /* older Swiper builds without autoplay control */
    }
  }

  /* A11Y-05 (#112): the pause the visitor chose, kept for the visit. sessionStorage can throw
     (Safari private mode, storage blocked); the pause then holds for this page only. */
  var HELD_KEY = 'woopf-announcements-paused';

  function readHeld() {
    try {
      return window.sessionStorage.getItem(HELD_KEY) === '1';
    } catch (err) {
      return false;
    }
  }

  function writeHeld(held) {
    try {
      if (held) window.sessionStorage.setItem(HELD_KEY, '1');
      else window.sessionStorage.removeItem(HELD_KEY);
    } catch (err) {
      /* storage blocked: the pause still holds on this page */
    }
  }

  function showHeld(toggle, held) {
    var label = toggle.querySelector('[data-ab-toggle-label]');
    var pauseIcon = toggle.querySelector('[data-ab-toggle-pause]');
    var playIcon = toggle.querySelector('[data-ab-toggle-play]');
    if (label) label.textContent = toggle.getAttribute(held ? 'data-label-play' : 'data-label-pause');
    if (pauseIcon) pauseIcon.hidden = held;
    if (playIcon) playIcon.hidden = !held;
  }

  function initBar(bar) {
    if (bar.dataset.ready === 'true') return;
    bar.dataset.ready = 'true';

    var frame = frameOf(bar);
    var slides = bar.querySelectorAll('.s-announcement-bar__item');
    var autoplayWanted = frame ? frame.dataset.autoplay === 'true' : true;
    var delay = frame ? parseInt(frame.dataset.delay, 10) || 6000 : 6000;
    var canAutoplay = autoplayWanted && !REDUCED.matches && slides.length > 1;

    if (frame) frame.style.setProperty('--ab-delay', delay + 'ms');

    if (typeof window.Swiper !== 'function') return;

    var options = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 400,
      loop: slides.length > 1,
      allowTouchMove: slides.length > 1,
      /* A11Y-04 (#111): the a11y module was switched off, so the inactive slide's link sat in the
         tab order, translated off-screen. On, its scrollOnFocus slides a focused link into view.
         (inert was rejected: with no arrows it would make the other message unreachable.) */
      a11y: { enabled: true, scrollOnFocus: true },
      on: {
        init: function () {
          startProgress(frame);
        },
        slideChange: function () {
          startProgress(frame);
        }
      }
    };

    if (canAutoplay) {
      options.autoplay = { delay: delay, disableOnInteraction: false, pauseOnMouseEnter: false };
    }

    bar.swiper = new window.Swiper(bar, options);

    if (!canAutoplay) return;

    /* A11Y-05 (#112): WCAG 2.2.2. Hover, focus and a hidden tab still pause the rotation, but only
       the visitor's own control ([data-ab-toggle], rendered hidden by the section) can hold it:
       `held` outranks every automatic resume below and is kept for the visit. */
    var held = readHeld();
    var toggle = frame ? frame.querySelector('[data-ab-toggle]') : null;

    var pause = function () { setPaused(frame, true); };
    var resume = function () { if (!held) setPaused(frame, false); };

    if (toggle) {
      toggle.hidden = false;
      showHeld(toggle, held);
      toggle.addEventListener('click', function () {
        held = !held;
        writeHeld(held);
        showHeld(toggle, held);
        setPaused(frame, held);
      });
    }

    if (held) setPaused(frame, true);

    bar.addEventListener('mouseenter', pause);
    bar.addEventListener('mouseleave', resume);
    bar.addEventListener('focusin', pause);
    bar.addEventListener('focusout', resume);

    if (frame) {
      var pin = frame.querySelector('.s-announcement-bar__pin');
      if (pin) {
        pin.addEventListener('mouseenter', pause);
        pin.addEventListener('mouseleave', resume);
        pin.addEventListener('focus', pause);
        pin.addEventListener('blur', resume);
      }
    }

    document.addEventListener('visibilitychange', function () {
      setPaused(frame, document.hidden || held);
    });
  }

  if (!customElements.get('s-announcement-bar')) {
    customElements.define(
      's-announcement-bar',
      class SectionAnnouncementBar extends HTMLElement {
        connectedCallback() {
          // Swiper may load after the element upgrades; retry briefly.
          var self = this;
          var tries = 0;
          var attempt = function () {
            if (typeof window.Swiper === 'function' || tries > 20) {
              initBar(self);
              return;
            }
            tries += 1;
            window.setTimeout(attempt, 100);
          };
          attempt();
        }

        disconnectedCallback() {
          if (this.swiper && typeof this.swiper.destroy === 'function') {
            try { this.swiper.destroy(true, true); } catch (err) { /* noop */ }
          }
          delete this.dataset.ready;
        }
      }
    );
  }
})();
